<script setup>
import { nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

import { DEV_MODE } from '@/config/dev-mode';

import { fetchBaseServiceAPI } from '@/api/baseServiceApi.js';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';

import { CONTENTS as MONTHLY_FORTUNE_CONTENTS, CONTENTS_PAGE_MAP as MONTHLY_FORTUNE_SECTION_PAGE_MAP } from './constants';

import monthlyFortuneGuide01 from './GuideMonthlyFortune01.vue';
import monthlyFortuneGuide02 from './GuideMonthlyFortune02.vue';
import MonthlyFortuneContents01 from './ContentsMonthlyFortune01.vue';
import MonthlyFortuneContents02 from './ContentsMonthlyFortune02.vue';
import MonthlyFortuneContents03 from './ContentsMonthlyFortune03.vue';
import MonthlyFortuneContents04 from './ContentsMonthlyFortune04.vue';
import MonthlyFortuneContents05 from './ContentsMonthlyFortune05.vue';

import ContentsList from '@/components/saju-insight/common/ContentsList.vue';
import { isObjectEmpty } from '@/composables/functions';

const props = defineProps({
  updateSelectedDescNav: {
    type: Function,
  },
  hasPermission: {
    type: Boolean,
  },
});
const emit = defineEmits(['goPopAnalysis']);

const route = useRoute();

const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();

const { sajuData } = storeToRefs(dataStore);

// =============== 페이지 ===============
/** index값으로 페이지 이동 */
const updateIndexNo = (id) => {
  if (id < 3) {
    // 1, 2페이지는 가이드 페이지
    props.updateSelectedDescNav('');
  } else {
    // 3페이지부터는 결제 필요
    if (!props.hasPermission) {
      emit('goPopAnalysis');
      return;
    }
    // 결제 후에는 오른쪽 패널 표시 (personalityJob 사용)
    props.updateSelectedDescNav('personalityJob');
  }
  indexNo.value = id;
};

// 인덱스
const indexNo = ref(1);
watch(indexNo, (newIndex) => {
  store.setMonthlyFortuneIndex(newIndex);
});
onUnmounted(() => {
  store.setMonthlyFortuneIndex(1);
});

watch(
  () => store.selectedContentIndex,
  (newIdx) => {
    const target = MONTHLY_FORTUNE_SECTION_PAGE_MAP[newIdx];
    if (target) {
      updateIndexNo(target);
    } else if (newIdx === null) {
      // selectedContentIndex가 null이면 기본값으로 1페이지 표시
      updateIndexNo(1);
    }
  },
  { immediate: true },
);

/** 오버뷰로 이동 */
const goToOverView = () => {
  store.clickMainMenu('/overviewMonthlyFortune');
};

// =============== 낭만월운 데이터 상태 ===============
const guestId = route.params.id;

const isFetchingMonthlyFortune = ref(true);

const monthlyFortuneParams = computed(() => {
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return null;

  return {
    guestUserId: guestId,
    lang: 'KOR',
    birth: sajuData.value.solar ?? null,
    userName: sajuData.value.inputdata.name ?? '',
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 10, // 월운세(낭만월운)
  };
});

const isReadyToShow = computed(() => {
  // 데이터 로딩이 완료되면 표시 (데이터가 없어도 표시)
  return !isFetchingMonthlyFortune.value;
});

/** 낭만월운 데이터 초기화 */
const initMonthlyFortuneData = async () => {
  if (!props.hasPermission) {
    await fetchSubData();
  } else {
    await fetchMonthlyFortuneData();
  }
};

/** 낭만월운 무료 데이터 */
const fetchSubData = async () => {
  isFetchingMonthlyFortune.value = true;
  try {
    // TODO: 무료 데이터 구조에 맞게 수정 필요
    if (isObjectEmpty(dataStore.monthlyFortuneSubData)) {
      const subDataParams = {
        ...monthlyFortuneParams.value,
        analysisTypeId: 8,
      };

      const result = await fetchBaseServiceAPI(subDataParams);
      // TODO: 데이터 구조에 맞게 수정 필요
      // dataStore.setMonthlyFortuneSubData(result?.data?.월운종합분석);
    }
  } catch (e) {
  } finally {
    isFetchingMonthlyFortune.value = false;
  }
};

/** 낭만월운 데이터 요청 */
const fetchMonthlyFortuneData = async () => {
  isFetchingMonthlyFortune.value = true;
  try {
    if (isObjectEmpty(dataStore.monthlyFortuneData)) {
      const result = await fetchBaseServiceAPI(monthlyFortuneParams.value);

      // TODO: 데이터 구조에 맞게 수정 필요
      // dataStore.setMonthlyFortuneData(result?.data);
      store.setAnalysisResult(result?.data);
    }
  } catch (error) {
    DEV_MODE && console.error('/monthlyFortune.vue fetchMonthlyFortuneData 실패:', error);
  } finally {
    isFetchingMonthlyFortune.value = false;
  }
};

watch(
  sajuData,
  async (newData, oldData) => {
    if (!isObjectEmpty(newData)) {
      await initMonthlyFortuneData();
    }
  },
  { immediate: true },
);

watch(
  () => props.hasPermission,
  async (newValue, oldValue) => {
    if (!isObjectEmpty(sajuData.value) && !oldValue && newValue) {
      await initMonthlyFortuneData();
    }
  },
);
</script>

<template>
  <div v-if="store.selectedMenu.endsWith('monthly-fortune')" class="full-height">
    <div v-if="isFetchingMonthlyFortune" class="analysis__loading-overlay full-height">
      <div class="loading-olverlay__img"></div>
      낭만월운 분석중입니다!
    </div>
    <monthlyFortuneGuide01 v-if="indexNo === 1" class="full-height" />
    <monthlyFortuneGuide02 v-if="indexNo === 2" class="full-height" />
    <MonthlyFortuneContents01
      v-if="indexNo === 3 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
    />
    <MonthlyFortuneContents02
      v-if="indexNo === 4 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
    />
    <MonthlyFortuneContents03
      v-if="indexNo === 5 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
    />
    <MonthlyFortuneContents04
      v-if="indexNo === 6 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
    />
    <MonthlyFortuneContents05
      v-if="indexNo === 7 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
    />
  </div>

  <div class="contentsListBtnBox">
    <ContentsList
      :items="MONTHLY_FORTUNE_CONTENTS"
      :curPage="indexNo"
      :hasPermission="hasPermission"
      @updateIndexNo="updateIndexNo"
      @goToOverView="goToOverView"
    />
  </div>
</template>

<style scoped lang="scss">
.full-height {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
}
.analysis__loading-overlay {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  font-size: 18px;
  font-weight: 700;
}
.monthly-fortune-content {
  padding: 40px;
}
</style>
<style lang="scss">
@import '/src/assets/nangman_style/personality.scss';
</style>
